import { CommonModule } from '@angular/common';
import { Component, computed, input, OnInit, signal, ViewChild } from '@angular/core';
import {
  ActionBarRight,
  AwActionBarComponent,
  AwDividerComponent,
  AwSpinnerComponent,
  AwToastComponent,
  BreadCrumb,
  SingleSelectOption,
  ToastVariants,
} from '@assetworks-llc/aw-component-lib';
import { AwBreadCrumbComponent } from '@assetworks-llc/aw-component-lib';
import { AwTextFormComponent } from '../../../../../shared/components/aw-text-form/aw-text-form.component';
import { AwDropdownFormComponent } from '../../../../../shared/components/aw-dropdown-form/aw-dropdown-form.component';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize, forkJoin, Observable, of, switchMap, mergeMap } from 'rxjs';
import { AwAuthFormComponent } from '../../../components/aw-auth-form/aw-auth-form.component';
import {
  AuthInputValues,
  AuthTypeValues,
  DEFAULT_AUTH_VALUES,
} from '../../../../../types/types';
import { ParentIntegrationService } from '../../../../../core/services/parent-integration.service';
import { EmployeeAuthenticationService } from '../../../../../core/services/employee-authentication.service';
import { SecretService } from '../../../../../core/services/secret.service';
import { AuthenticationConfiguration, CreateSecretRequest } from '../../../../../api/models';
import { Organization, UserService } from '../../../../../core/services/user.service';

@Component({
  selector: 'app-edit-parent-integration-details',
  imports: [
    AwDividerComponent,
    AwBreadCrumbComponent,
    AwTextFormComponent,
    CommonModule,
    AwSpinnerComponent,
    AwActionBarComponent,
    AwDropdownFormComponent,
    AwToastComponent,
    AwAuthFormComponent,
  ],
  templateUrl: './edit-parent-integration-details.component.html',
  styleUrls: [
    './edit-parent-integration-details.component.scss',
    '../../../../../../styles/management.scss',
  ],
  standalone: true,
})
export class EditParentIntegrationDetailsComponent implements OnInit {
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private parentIntegrationService: ParentIntegrationService,
    private employeeAuthenticationService: EmployeeAuthenticationService,
    private secretService: SecretService,
    private userService: UserService
  ) {}

  @ViewChild(AwToastComponent) toastComponent!: AwToastComponent;

  isLoading: boolean = false;
  parentIntegrationId: string = '';
  organization: Organization | null = null;

  nameInput: string = '';
  statusInput = signal<string>('');
  statusValue!: boolean;
  currentIntegrationIds: string[] = [];

  initialName = signal<string>('');
  initialStatus = signal<boolean>(false);

  employeeAuthTypeValue: AuthTypeValues = AuthTypeValues.OPENIDCONNECT;
  employeeAuthFieldsValues: AuthInputValues = DEFAULT_AUTH_VALUES;
  employeeAuthFieldsFilled: boolean = false;
  employeeAuthModified: boolean = false;
  hasEmployeeAuthConfig: boolean = false;

  breadcrumbs = signal<BreadCrumb[]>([
    {
      label: 'Parent Integration Management',
      link: '/parent-integration-management/overview',
      newTab: false,
    },
  ]);

  statusOptions = signal<SingleSelectOption[]>([
    { label: 'Active' },
    { label: 'Disabled' },
  ]);

  selectedStatusOption = computed(() => {
    const label = this.statusInput();
    return this.statusOptions().find(opt => opt.label === label) || null;
  });

  actionsRight = input<ActionBarRight[]>([
    {
      buttonCallback: {
        label: 'Cancel',
        buttonType: 'primary',
        action: () => this.router.navigate(['/parent-integration-management/parent-integration-details', this.parentIntegrationId]),
      },
    },
    {
      buttonCallback: {
        label: 'Save',
        buttonType: 'outlined',
        action: () => this.updateParentIntegration(),
      },
    },
  ]);

  ngOnInit(): void {
    this.isLoading = true;
    this.parentIntegrationId = this.route.snapshot.paramMap.get('parentIntegrationId')!;
    this.organization = this.userService.getOrganization();

    this.breadcrumbs.set([
      ...this.breadcrumbs(),
      {
        label: 'Parent Integration Details',
        link: `/parent-integration-management/parent-integration-details/${this.parentIntegrationId}`,
        newTab: false,
      },
      {
        label: 'Edit',
        link: `/parent-integration-management/parent-integration-details/${this.parentIntegrationId}/edit`,
        newTab: false,
      },
    ]);

    forkJoin({
      parentIntegration: this.parentIntegrationService.getParentIntegrationById({
        organizationId: this.parentIntegrationId,
      }),
      employeeAuth: this.employeeAuthenticationService.getAuthenticationConfig({
        organizationId: this.parentIntegrationId,
      }),
    }).subscribe({
      next: ({ parentIntegration, employeeAuth }) => {
        if (parentIntegration) {
          this.nameInput = parentIntegration.name || '';
          this.statusValue = parentIntegration.enabled || false;
          this.statusInput.set(parentIntegration.enabled ? 'Active' : 'Disabled');
          this.currentIntegrationIds = parentIntegration.integrationIds || [];
          this.initialName.set(this.nameInput);
          this.initialStatus.set(this.statusValue);
        }

        if (employeeAuth) {
          this.hasEmployeeAuthConfig = true;
          this.employeeAuthFieldsValues = this.extractAuthValues(employeeAuth);
        } else {
          this.hasEmployeeAuthConfig = false;
        }

        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        this.buildToastMessage('Failed to load parent integration data', 'error');
      },
    });
  }

  extractAuthValues(authConfig: AuthenticationConfiguration): AuthInputValues {
    const config = authConfig.openIdConnectConfiguration;
    return {
      auth_endpoint: config?.authorizationEndpoint || '',
      token_endpoint: config?.tokenEndpoint || '',
      logout_endpoint: config?.logoutEndpoint || '',
      clientId: config?.clientId || '',
      grant_type: config?.grantType || 'code',
      scope: config?.scope || '',
      client_secret: '',
      ext_userId_claim: config?.externalUserIdClaimName || '',
      self_discovery_endpoint: config?.selfDiscoveryEndpoint || '',
    };
  }

  onNameSelected(text: string | null) {
    this.nameInput = text || '';
  }

  onStatusSelected(status: SingleSelectOption | null) {
    this.statusInput.set(status?.label || '');
    this.statusValue = status?.label === 'Active';
  }

  onEmployeeAuthValuesChanged(event: {
    values: AuthInputValues;
    fieldsFilled: boolean;
    someFieldsFilled: boolean;
  }) {
    this.employeeAuthFieldsFilled = event.fieldsFilled;
    this.employeeAuthFieldsValues = event.values;
    this.employeeAuthModified = event.someFieldsFilled;
  }

  buildToastMessage(message: string, variant: ToastVariants): void {
    this.toastComponent.showToast({
      variant: variant,
      title: message,
      description: '',
      removeIcon: true,
      removeX: false,
      alwaysShow: false,
      toastPositionTopLeft: false,
      multiToasts: false,
    });
  }

  updateParentIntegration(): void {
    if (this.organization === 'integration-employee') {
      if (!this.employeeAuthFieldsValues.client_secret) {
        this.buildToastMessage('No changes to save', 'alert');
        return;
      }
      this.updateEmployeeAuthOnly();
      return;
    }

    const hasChanges = this.nameInput !== this.initialName() || 
                       this.statusValue !== this.initialStatus() ||
                       this.employeeAuthFieldsValues.client_secret !== '';
    
    if (!hasChanges) {
      this.buildToastMessage('No changes to save', 'alert');
      return;
    }

    if (!this.nameInput || this.statusValue === null || this.statusValue === undefined) {
      this.buildToastMessage('Fill in all required fields', 'error');
      return;
    }

    if (this.employeeAuthModified && !this.employeeAuthFieldsFilled) {
      this.buildToastMessage('Fill in all employee authentication fields', 'error');
      return;
    }

    this.isLoading = true;

    this.parentIntegrationService
      .updateParentIntegration({
        organizationId: this.parentIntegrationId,
        body: {
          name: this.nameInput,
          enabled: this.statusValue,
          integrationIds: this.currentIntegrationIds,
        },
      })
      .pipe(
        switchMap((updateResult) => {
          if (!this.employeeAuthModified) {
            return of(null);
          }

          const secretName = 'employeeOpenIdClientSecret';
          const secretOp: Observable<any> = this.employeeAuthFieldsValues.client_secret
            ? this.secretService.createSecret({
                organizationId: this.parentIntegrationId,
                body: {
                  secretName: secretName,
                  secretValue: this.employeeAuthFieldsValues.client_secret,
                } as CreateSecretRequest,
              })
            : of(null);

          return secretOp.pipe(
            mergeMap(() => this.employeeAuthenticationService.updateAuthenticationConfig({
              organizationId: this.parentIntegrationId,
              authType: this.employeeAuthTypeValue,
              authConfigValues: this.employeeAuthFieldsValues,
              secretName: secretName,
            }))
          );
        })
      )
      .subscribe({
        next: () => {
          this.isLoading = false;
          this.buildToastMessage('Successfully updated parent integration', 'success');
          this.router.navigate([
            '/parent-integration-management/parent-integration-details',
            this.parentIntegrationId,
          ]);
        },
        error: () => {
          this.isLoading = false;
          this.buildToastMessage('Failed to update parent integration', 'error');
        },
      });
  }

  updateEmployeeAuthOnly(): void {
    this.isLoading = true;
    
    this.secretService.createSecret({
      organizationId: this.parentIntegrationId,
      body: {
        secretName: 'employeeOpenIdClientSecret',
        secretValue: this.employeeAuthFieldsValues.client_secret ?? '',
      }
    }).pipe(finalize(() => this.isLoading = false))
      .subscribe({
        next: () => {
          this.buildToastMessage('Successfully updated employee authentication', 'success');
          this.router.navigate(['/parent-integration-management/parent-integration-details', this.parentIntegrationId]);
        },
        error: () => {
          this.buildToastMessage('Failed to update employee authentication', 'error');
        }
      });
  }
}
